import { ErrorCatcher } from '../../tools/error-catcher';
import { getImageServiceSrc } from '../../tools/get-image-src';
import { useAppContext } from '../../app-context-provider/app-context-provider';
import { ImageWordpress } from '../../types/shared';
import dayjs from 'dayjs';

export const getImageObject = (imageObject: ImageWordpress | null | undefined) => {
    if (!imageObject) {
        return undefined;
    }
    const imageServiceSrc = getImageServiceSrc(imageObject);
    const width = imageObject.width;
    const height = imageObject.height;

    return {
        '@type': 'ImageObject',
        '@id': imageServiceSrc,
        url: imageServiceSrc,
        height: height,
        width: width,
    };
};

export const getPublisherObject = () => {
    const { siteLogo, siteInfo } = useAppContext();
    return {
        '@type': 'Organization',
        '@id': siteInfo.baseurl,
        name: 'TwoFour',
        logo: siteLogo ? getImageObject(siteLogo) : null,
    };
};

const articleSchema = (content: any) => {
    const { siteInfo } = useAppContext();
    return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        url: siteInfo.baseurl + content?.permalink,
        publisher: getPublisherObject(),
        name: content?.post_title,
        description: content?.list_description,
        articleBody: content?.stripped_content,
        datePublished: dayjs(new Date(content?.date)).format('YYYY-MM-DD'),
        image: content?.featured_image ? [getImageServiceSrc(content.featured_image)] : [],
    };
};

const organizationSchema = () => {
    const { siteInfo, translate } = useAppContext();

    return {
        '@context': 'https://schema.org/',
        '@type': 'Organization',
        'name': 'TwoFour AB',
        'url': siteInfo.baseurl,
        'email': 'malmo@twofour.se',
        'telephone': '+46406020700',
        'slogan': translate('twofour-slogan'),
        'logo': siteInfo.baseurl +'/assets/twofour-logo.png',
        'address': {
            '@type': 'PostalAddress',
            addressLocality: 'Malmö, Sweden',
            postalCode: '21139',
            streetAddress: 'Gustav Adolfs torg 8b',
            addressCountry: 'Sweden',
        },
        'contactPoint': [
            {
                '@type': 'ContactPoint',
                telephone: '+46406020700',
                areaServed: ['SV', 'DK', 'NO'],
                availableLanguage: ['Swedish', 'English', 'Danish', 'Norwegian'],
            },
        ],
        'sameAs': [
            'https://www.linkedin.com/company/twofour-digital',
            'https://www.instagram.com/twofourab'
        ]
    };
};

const getSchemaContent = (content: any) => {
    let schemaContent = '';

    if (content?.permalink === '' ||
        content?.permalink?.startsWith('/about-us') ||
        content?.permalink?.startsWith('/om-oss') ||
        content?.permalink?.startsWith('/contact') ||
        content?.permalink?.startsWith('/kontakt')) {
        schemaContent = JSON.stringify(organizationSchema());
    } else if (content?.type === 'project') {
        schemaContent = JSON.stringify(articleSchema(content));
    }
    return schemaContent;
};

export const Schema = (props: any) => {
    return (
        <ErrorCatcher componentName="Schema">
            <script type="application/ld+json">{getSchemaContent(props.content)}</script>
        </ErrorCatcher>
    );
};
